<template>
    <div id="VehicleSelect" v-if="isSearchSet">
        <progress-header />

        <div class="container mt-4">
            <div class="row justify-content-center pt-4" v-if="isLoading">
                <div class="spinner-border" role="status">
                    <span class="sr-only">{{ $t('loading') }}...</span>
                </div>
            </div>

            <div class="text-center" v-else-if="travels.length === 0">
                {{ $t('taxi.pages.vehicles.no_transfers_found') }}
            </div>

            <div class="row" v-if="travels.length > 0 && travels[0].is_short_time">
                <div class="col-12 col-lg-9">
                    <div class="alert alert-warning text-center" v-html="$t('taxi.pages.vehicles.short_time')" />
                </div>
            </div>

            <div class="row" v-if="isLoaded && travels.length > 0">
                <div class="col-12 col-lg-9 mb-2">
                    <div class="card booking-form-card shadow-sm">
                        <div class="card-header">
                            <span class="icon position-pin-icon" />

                            {{ search.start_place }}
                            <font-awesome-icon icon="long-arrow-alt-right" class="in-text-icon" />
                            {{ search.end_place }}
                        </div>

                        <div
                            class="row py-4 border-bottom travel-result"
                            :class="{'is-eco': travel.vehicle.is_eco}"
                            v-for="travel in travels"
                            :key="travel.id"
                        >
                            <div class="col-6 col-md-4">
                                <img :src="travel.vehicle.photo_url" :alt="travel.vehicle.name" class="img-fluid vehicle-image">
                            </div>

                            <div class="col-6 col-md-4 px-sm-0">
                                <div class="vehicle-name" :class="{'is-eco': travel.vehicle.is_eco}">
                                    BERBUS
                                </div>

                                <div class="d-none d-md-block" v-if="travel.vehicle.description">
                                    <div>
                                        {{ travel.vehicle.description }}
                                    </div>
                                </div>
                            </div>

                            <div class="col-6 mt-2 d-block d-md-none" v-if="travel.vehicle.description">
                                <div>
                                    {{ travel.vehicle.description }}
                                </div>
                            </div>

                            <div class="col-6 col-md-4 text-right">
                                <div class="text-lg text-blue-600 mt-2 gross-price">
                                    <template
                                        v-if="travel.promo_price && travel.gross_price !== travel.promo_price.gross_price"
                                    >
                                        <del class="d-block">
                                            {{ formatPrice(travel.gross_price, travel.currency) }} {{ $t('berbus.pages.vehicle.price_per_person') }}
                                        </del>

                                        {{ formatPrice(travel.promo_price.gross_price, travel.currency) }} {{ $t('berbus.pages.vehicle.price_per_person') }}
                                    </template>

                                    <template v-else>
                                        {{ formatPrice(travel.gross_price, travel.currency) }}
                                        <span class="text-sm" v-if="false">{{ $t('berbus.pages.vehicle.price_per_person') }}</span>
                                    </template>
                                </div>

                                <div class="text-sm text-muted">
                                    <div class="net-price">
                                        <template v-if="travel.promo_price && travel.net_price !== travel.promo_price.net_price">
                                            <del
                                                class="d-block"
                                                v-if="travel.promo_price && travel.net_price !== travel.promo_price.net_price"
                                            >
                                                {{ formatPrice(travel.net_price, travel.currency) }}
                                            </del>

                                            {{ formatPrice(travel.promo_price.net_price, travel.currency) }} {{ $t('net') }}
                                        </template>

                                        <template v-else>
                                            {{ formatPrice(travel.net_price, travel.currency) }} {{ $t('net') }}
                                            <span class="" v-if="false">{{ $t('berbus.pages.vehicle.price_per_person') }}</span>
                                        </template>
                                    </div>
                                </div>

                                <button class="btn btn-primary select-button" @click="handlePickTravel(travel)">
                                    {{ $t('select') }}
                                </button>

                                <div class="mt-4">
                                    <number-box :number="passengersTotal" icon="seat" class="d-inline-block" />
                                    <number-box :number="passengersTotal" icon="suitcase" class="d-inline-block" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <v-dialog />

                <div class="col-12 col-lg-3" v-if="travels.length > 0">
                    <the-cart />
                </div>

                <div class="col-12 col-lg-9">
                    <stop-info />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';

    import TheCart from './components/Cart/TheCart';
    import ProgressHeader from './components/ProgressHeader';
    import NumberBox from '@components/NumberBox';
    import {trackGA} from '@utilities/tracking';
    import StopInfo from '@/pages/BerbusPage/components/StopInfo.vue';
    import backofficeRepository from '@repositories/BackofficeRepository';
    import {getCurrency} from '@utilities/helpers';
    import {BERBUS_PLACE_VENDOR_GMAPS, BERBUS_PLACE_VENDOR_LOCAL} from '@/constants';

    export default {
        components: {
            StopInfo,
            TheCart,
            ProgressHeader,
            NumberBox,
        },

        data() {
            return {
                isLoading: true,
                isLoaded: false,

                travels: [],
            };
        },

        computed: {
            ...mapGetters({
                isSearchSet: 'berbus/isSearchSet',
                search: 'berbus/search',
                passengersTotal: 'berbus/passengersTotal',
                vendorAddresses: 'berbus/vendorAddresses',
            }),
        },

        created() {
            trackGA('checkout_progress', {
                checkout_step: 3,
                checkout_option: window.location.pathname,
            });

            if (!this.isSearchSet) {
                this.$router.push({ name: 'berbus.index' });

                return;
            }

            this.loadTravels();
        },

        methods: {
            loadTravels() {
                this.isLoading = true;
                this.isLoaded = false;

                const passengers = [];
                const berlinRegex = new RegExp(/Berlin Brandenburg/i);
                const vendorStart = !berlinRegex.test(this.search.start_place)?this.vendorAddresses.start:{street:this.search.start_place, city: null, zip: null, geo: null};
                const vendorEnd = !berlinRegex.test(this.search.end_place)?this.vendorAddresses.end:{street:this.search.end_place, city: null, zip: null, geo: null};
                this.search.passengers.forEach(p => {
                    for(let i=0; i<p.quantity; i++){
                        passengers.push({ type: p.type==='adult'?'ADULT':'CHILD_BELOW_4' });
                    }
                });
                //convert departure_date to timestamp
                const stops = [
                    backofficeRepository.mapAddress(vendorStart, vendorStart.geo, new Date(this.search.departure_date + ' ' + this.search.departure_time).getTime()/1000),
                    backofficeRepository.mapAddress(vendorEnd, vendorEnd.geo),
                ];

                backofficeRepository.calculateOrderValueByClient('BERBUS', passengers, stops).then(( response ) => {
                    this.travels = [{
                        code: 1,
                        currency: getCurrency(),
                        end_is_airport: this.search.end_place_id.indexOf('_AIRPORT') > -1,
                        end_place: this.search.end_place,
                        gross_price: response.value.grossPriceWithoutDiscount * 100,
                        id: 1,
                        is_short_time: false,
                        net_price: (response.value.grossPriceWithoutDiscount - response.value.vat) * 100,
                        start_is_airport: this.search.start_place_id.indexOf('_AIRPORT') > -1,
                        start_place: this.search.start_place,
                        vat: response.value.vat * 100,
                        vehicle_id: 1,
                        vehicle:
                            {
                                id: 1,
                                code: 1,
                                name: 'Ridesharing',
                                brand: '',
                                description: this.$t('berbus.pages.vehicle.description'),
                                passengers_number_min: 1,
                                passengers_number_max: 'vehicle.vehicleInfo.passengersCount',
                                hand_luggage_amount: 'vehicle.vehicleInfo.luggageCount',
                                checked_baggage_amount: 'vehicle.vehicleInfo.luggageCount',
                                is_available_in_48h: false,
                                is_visible: true,
                                is_eco: false,
                                order: 1,
                                photo_url: '/images/berbus-description.jpg',
                            },
                    }];
                }).finally(() => {
                    this.isLoading = false;
                    this.isLoaded = true;
                });
            },

            handlePickTravel(travel) {
                this.$store.dispatch('berbus/setTravel', travel).then(() => {
                    this.$router.push({ name: 'berbus.passengers' });
                });
            },
        },
    };
</script>

